/**
 * useSimWorker — React-aware adapter for the Monte Carlo Web Worker
 * defined in `apps/web/src/lib/pilot/sim-worker.ts`.
 *
 * The worker speaks a typed discriminated union (`SimWorkerEvent`) over
 * `postMessage`. This hook owns a single store backed by a module-level
 * Worker instance, subscribes components via `useSyncExternalStore`, and
 * lets multiple components in the Sandbox tree read the same in-flight
 * run without re-creating the Worker.
 *
 * Phase 5 of the pilot React-engine port (see
 * `docs/eng/pilot-react-engine.md`). Risk R1 in that plan named this
 * hook's contract as load-bearing: the union type is the single source
 * of truth and lives next to the worker in `sim-worker.ts`. Adding a
 * new event shape there requires updating this hook in lockstep.
 */

import {useCallback, useSyncExternalStore} from "react";
import type {
  SimWorkerEvent,
  SimWorkerInbound,
} from "../lib/pilot/sim-worker";
import type {Intake, ProcessDef} from "../lib/pilot/processes";
import type {RunAllResult} from "../lib/pilot/simulation";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Lifecycle status of the most recent / in-flight run. */
export type SimWorkerStatus = "idle" | "running" | "ready" | "error";

export interface SimWorkerProgress {
  /** Completed trials. */
  done: number;
  /** Total trials this run will perform. */
  total: number;
  /** done / total, clamped to [0, 1]. */
  fraction: number;
}

export interface SimWorkerSnapshot {
  status: SimWorkerStatus;
  /** Last successful result. Preserved while a new run is in flight so
   *  the UI keeps the previous numbers visible during recompute. */
  results: RunAllResult | null;
  /** Latest progress emit; null when no run has started yet. */
  progress: SimWorkerProgress | null;
  /** Latest worker-error message; null when no error has happened. */
  error: string | null;
}

export interface SimWorkerRunArgs {
  proc: ProcessDef;
  intake: Partial<Intake> | null | undefined;
  params: Record<string, number>;
  n?: number;
  winProbN?: number;
  breakdownN?: number;
}

export interface UseSimWorker extends SimWorkerSnapshot {
  /** Enqueue a new full run, cancelling any in-flight predecessor. */
  run: (args: SimWorkerRunArgs) => void;
  /** Cancel the currently in-flight run, if any. */
  cancel: () => void;
}

// ---------------------------------------------------------------------------
// Module-level store
// ---------------------------------------------------------------------------
//
// One Worker per app. Subscribers register a listener; the store
// recomputes a snapshot whenever a worker event arrives and notifies
// every subscriber.

let _worker: Worker | null = null;
let _nextId = 0;
let _activeId = 0;

const _listeners = new Set<() => void>();

let _snapshot: SimWorkerSnapshot = {
  status: "idle",
  results: null,
  progress: null,
  error: null,
};

function getSnapshot(): SimWorkerSnapshot {
  return _snapshot;
}

function subscribe(cb: () => void): () => void {
  _listeners.add(cb);
  return () => {
    _listeners.delete(cb);
  };
}

function emit(next: SimWorkerSnapshot): void {
  _snapshot = next;
  _listeners.forEach((cb) => {
    try {
      cb();
    } catch {
      /* swallow — one bad listener shouldn't take down the rest */
    }
  });
}

function ensureWorker(): Worker {
  if (_worker) return _worker;
  // Same URL the legacy `runAllAsync` in simulation.ts uses — keeps
  // Webpack's worker chunking single-sourced.
  _worker = new Worker(
    new URL("../lib/pilot/sim-worker.ts", import.meta.url),
    {type: "module"},
  );
  _worker.addEventListener("message", (e: MessageEvent) => {
    const msg = e.data as SimWorkerEvent | null | undefined;
    if (!msg || typeof msg !== "object" || typeof msg.id !== "number") return;
    // Stale runs (cancelled by a newer run() call) are silently dropped
    // so a delayed `result` doesn't overwrite the active run's progress.
    if (msg.id !== _activeId) return;
    if (msg.type === "progress") {
      const total = msg.total || 1;
      const fraction = Math.max(0, Math.min(1, msg.done / total));
      emit({
        ..._snapshot,
        status: "running",
        progress: {done: msg.done, total: msg.total, fraction},
      });
    } else if (msg.type === "result") {
      emit({
        status: "ready",
        results: msg.data,
        progress: {
          done: msg.data.human.n,
          total: msg.data.human.n,
          fraction: 1,
        },
        error: null,
      });
    } else if (msg.type === "error") {
      // Drop the cached singleton so the next run() spawns a fresh
      // worker. The error may have left this instance in an unknown
      // state (postMessage to a terminated worker, internal exception
      // mid-run); reusing it risks a silent no-op on the retry.
      _terminateWorker();
      emit({
        status: "error",
        results: _snapshot.results,
        progress: _snapshot.progress,
        error: msg.error,
      });
    }
  });
  _worker.addEventListener("error", (e: ErrorEvent) => {
    // Same reasoning as the `msg.type === "error"` path above.
    _terminateWorker();
    emit({
      status: "error",
      results: _snapshot.results,
      progress: _snapshot.progress,
      error: e.message || "sim-worker crashed",
    });
  });
  return _worker;
}

// Tear down the singleton so the next ensureWorker() spawns a fresh
// instance. Called from the error paths above and from
// __testing.reset(). Best-effort: `terminate()` may throw if the
// worker already died, which is fine — we still null the ref.
function _terminateWorker(): void {
  if (_worker) {
    try {
      _worker.terminate();
    } catch {
      /* worker already dead */
    }
    _worker = null;
  }
  _activeId = 0;
}

function runStore(args: SimWorkerRunArgs): void {
  const worker = ensureWorker();
  // Cancel any in-flight run (we only ever care about the newest set
  // of knob values).
  if (_activeId > 0) {
    const cancel: SimWorkerInbound = {op: "cancel", id: _activeId};
    worker.postMessage(cancel);
  }
  const id = ++_nextId;
  _activeId = id;
  emit({
    status: "running",
    // Keep the prior results visible until the new run produces fresh
    // numbers — the page shouldn't blank out on every knob nudge.
    results: _snapshot.results,
    progress: {done: 0, total: args.n ?? 10000, fraction: 0},
    error: null,
  });
  const msg: SimWorkerInbound = {
    op: "runAll",
    id,
    proc: args.proc,
    intake: args.intake,
    params: args.params,
    n: args.n,
    winProbN: args.winProbN,
    breakdownN: args.breakdownN,
  };
  worker.postMessage(msg);
}

function cancelStore(): void {
  if (!_worker || _activeId === 0) return;
  const cancel: SimWorkerInbound = {op: "cancel", id: _activeId};
  _worker.postMessage(cancel);
  emit({
    ..._snapshot,
    status: _snapshot.results ? "ready" : "idle",
  });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Subscribe a component to the sim worker's results.
 *
 * Components that only need to *read* the latest result (e.g. the stat
 * bars) and components that need to *kick off* runs (e.g. the
 * `<KnobGrid>` after a slider change-event) share the same store, so a
 * new run from one component is observed by every subscriber.
 */
export function useSimWorker(): UseSimWorker {
  const snapshot = useSyncExternalStore(
    subscribe,
    getSnapshot,
    // SSR fallback — Pilot doesn't SSR today but CRA's type-check still
    // exercises this signature.
    getSnapshot,
  );
  const run = useCallback((args: SimWorkerRunArgs) => {
    runStore(args);
  }, []);
  const cancel = useCallback(() => {
    cancelStore();
  }, []);
  return {
    status: snapshot.status,
    results: snapshot.results,
    progress: snapshot.progress,
    error: snapshot.error,
    run,
    cancel,
  };
}

// ---------------------------------------------------------------------------
// Test seam — production code should never reach for these. They exist
// so a future unit test can reset the module's hidden state between
// cases without round-tripping through a Worker.
// ---------------------------------------------------------------------------

export const __testing = {
  reset(): void {
    _activeId = 0;
    _nextId = 0;
    _snapshot = {status: "idle", results: null, progress: null, error: null};
    _listeners.clear();
  },
};
